import React from "react";
import { cx } from "utils/join-classnames";
import {
    TextField,
    FileInput,
    PasswordField,
    Icon,
    CheckBox,
    TextArea,
    TagInput,
    Dropdown,
} from "ui/components";
import { DropdownOption } from "core/models";
import { FormField } from "ui/hooks/use-form-validation";
import { FormElementType } from "core/models/FormElement";
import styles from "./FormElement.module.scss";

interface Props {
    type: FormElementType;
    title: string;
    id: string;
    defaultText?: string;
    theme: "dark" | "light";
    fieldState?: FormField;
    disabled?: boolean;
    options?: DropdownOption[];
    checkBoxPos?: "left" | "right";
    onChange(value: string | string[] | File | undefined): void;
    className?: string;
    required?: boolean;
}
const FormElement: React.FC<Props> = ({
    title,
    type,
    defaultText,
    theme,
    id,
    fieldState,
    onChange,
    disabled,
    options,
    checkBoxPos,
    className,
    required,
}: Props) => {
    const renderElement = () => {
        switch (type) {
            case "text":
                return (
                    <TextField
                        value={fieldState?.value?.toString() || ""}
                        theme={theme}
                        defaultText={defaultText || ""}
                        id={id}
                        onValueChanged={onChange}
                        valid={
                            !fieldState?.touched ? undefined : fieldState.valid
                        }
                        disabled={disabled}
                        focus
                    />
                );
            case "password":
                return (
                    <PasswordField
                        theme={theme}
                        defaultText={defaultText || ""}
                        id={id}
                        onValueChanged={onChange}
                        valid={
                            !fieldState?.touched ? undefined : fieldState.valid
                        }
                        disabled={disabled}
                    />
                );
            case "tags":
                return (
                    <TagInput
                        id={id}
                        value={
                            Array.isArray(fieldState?.value) && fieldState
                                ? fieldState.value
                                : []
                        }
                        onChange={(value) => {
                            onChange(value);
                        }}
                        disabled={disabled}
                        valid={
                            !fieldState?.touched ? undefined : fieldState.valid
                        }
                    />
                );
            case "checkbox":
                return (
                    <CheckBox
                        theme={theme}
                        id={id}
                        value={fieldState?.value?.toString() || "false"}
                        title={title}
                        onChange={(val: boolean) => onChange(val.toString())}
                        valid={
                            !fieldState?.touched ? undefined : fieldState.valid
                        }
                        checkboxPos={checkBoxPos}
                    />
                );
            case "file":
                return (
                    <FileInput
                        theme={theme}
                        value={
                            fieldState?.value instanceof File
                                ? fieldState.value.name
                                : ""
                        }
                        defaultText={defaultText || "select a file"}
                        onValueChanged={onChange}
                        disabled={disabled}
                        valid={
                            !fieldState?.touched ? undefined : fieldState.valid
                        }
                    />
                );
            case "dropdown":
                return (
                    <Dropdown
                        theme={theme}
                        value={fieldState?.value?.toString()}
                        onValueChanged={(value) => onChange(value)}
                        options={options || []}
                        disabled={disabled}
                        fullHeight
                    />
                );
            case "textarea":
                return (
                    <TextArea
                        value={fieldState?.value?.toString()}
                        onValueChanged={(value) => onChange(value)}
                        disabled={disabled}
                        className={className}
                        valid={
                            !fieldState?.touched ? undefined : fieldState.valid
                        }
                    />
                );
            default:
                throw new Error("this should not happen");
        }
    };
    return (
        <div
            className={cx(
                styles.formelement,
                theme === "light"
                    ? styles.formelement__light
                    : styles.formelement__dark,
            )}
        >
            {type !== "checkbox" && (
                <label
                    htmlFor={id}
                    className={cx(
                        styles.formelement__label,
                        required ? styles.formelement__label_required : null,
                    )}
                >
                    {title}
                </label>
            )}

            <div className={styles.formelement__input}>{renderElement()}</div>
            {type !== "checkbox" && (
                <div className={styles.error__msg}>
                    {fieldState?.errorMsg && (
                        <span>
                            <Icon
                                name="error"
                                className={styles.error__msg__icon}
                            />{" "}
                            {fieldState.errorMsg}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default FormElement;
